/**
 * Extracts SKU from "R000271 - SESAME SEEDS 1KG" → "R000271"
 */
export const extractSku = (desc = "") => {
  const s = String(desc);
  const m = s.match(/^\s*([A-Za-z0-9\-]+)\s*-\s*/);
  if (m) return m[1].trim();
  return s.split(/\s+/)[0].trim(); // fallback
};

/**
 * Removes leading code so "R000271 - SESAME SEEDS 1KG" → "SESAME SEEDS 1KG"
 */
export const extractItemName = (desc = "") =>
  String(desc)
    .replace(/^\s*[A-Za-z0-9\-]+\s*-\s*/, "")
    .trim();

/**
 * Convert number-like value to money-ish string "56.73"
 */
export const toMoneyString = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

export const buildHeaderKeyMap = (rawHeaders = []) => {
  const map = {}; // { fileHeaderText : yourFieldKey }
  for (const h of rawHeaders) {
    const norm = normalizeHeaderText(h);
    const key = headerAliasMap[norm];
    if (key) map[h] = key;
  }
  return map;
};

/**
 * normalize helper you already use for headers
 */
export const normalizeHeaderText = (s = "") =>
  String(s)
    .replace(/\u00A0/g, " ")
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/**
 * Helper to parse numbers like ₱ 67,200.00
 */
export const toNumber = (v) => {
  if (v === undefined || v === null) return 0;
  const n = parseFloat(String(v).replace(/[₱,\s]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/**
 * File header variants -> internal field keys
 */
export const headerAliasMap = {
  "reference no": "referenceNo",
  "reference number": "referenceNo",
  referenceno: "referenceNo",
  "reference nonumber": "referenceNo",

  "product description": "productDescription",
  description: "productDescription",

  warehouse: "warehouse",
  whse: "warehouse",

  quantity: "quantity",
  qty: "quantity",

  unit: "unit",

  price: "price",
  "unit price": "price",

  discount: "discount",

  "net price": "netPrice",
  net: "netPrice",

  amount: "amount",
  "line amount": "amount",
};

/**
 * Read the DR header block (first rows) from an Excel AOA (array-of-arrays)
 */
export const extractDRHeader = (aoa) => {
  // Try map-by-label in column A with value in column B (A=0, B=1)
  const labelMap = {
    "dr no": "drNo",
    "dr no#": "drNo",
    date: "drDate",
    "dr type": "drType",
    "document type": "docType",
    remarks: "remarks",
  };

  const out = { drNo: "", drDate: "", drType: "", docType: "", remarks: "" };

  // scan first 15 rows for labels
  for (let r = 0; r < Math.min(15, aoa.length); r++) {
    const rawLabel = (aoa[r]?.[0] ?? "").toString();
    const rawValue = (aoa[r]?.[1] ?? "").toString().trim();
    const norm = normalizeHeaderText(rawLabel);
    const key = labelMap[norm];
    if (key) out[key] = rawValue;
  }

  // Fallback to fixed cells if still empty (matches your screenshot layout)
  out.drNo ||= (aoa[0]?.[1] || "").toString().trim();
  out.drDate ||= (aoa[1]?.[1] || "").toString().trim();
  out.drType ||= (aoa[2]?.[1] || "").toString().trim();
  out.docType ||= (aoa[3]?.[1] || "").toString().trim();
  out.remarks ||= (aoa[4]?.[1] || "").toString().trim();

  return out;
};

/**
 * For Excel AOA: find the header row (skips DR No, Date, etc.)
 */
export const findHeaderRow = (rows) => {
  const mustHave = [
    "reference no",
    "product description",
    "quantity",
    "amount",
  ];
  for (let i = 0; i < rows.length; i++) {
    const norms = rows[i].map(normalizeHeaderText);
    const hits = mustHave.every((mh) => norms.includes(mh));
    if (hits) return i;
  }
  return 0;
};

export const rowToItem = (rowObj, headerKeyMap) => {
  const out = {
    referenceNo: "",
    productDescription: "",
    warehouse: "",
    quantity: 0,
    unit: "",
    price: 0,
    discount: 0,
    netPrice: 0,
    amount: 0,
  };

  for (const [fileHeader, value] of Object.entries(rowObj)) {
    const field = headerKeyMap[fileHeader];
    if (!field) continue;

    switch (field) {
      case "quantity":
      case "price":
      case "discount":
      case "netPrice":
      case "amount":
        out[field] = this.toNumber(value);
        break;
      default:
        out[field] = String(value ?? "").trim();
    }
  }
  return out;
};

function getBrowserCrypto() {
  return typeof window !== "undefined" && window.crypto
    ? window.crypto
    : undefined;
}

/**
 * RFC4122 v4 UUID (uses Web Crypto if available, with safe fallbacks)
 */
export const makeUUID = () => {
  const c = getBrowserCrypto();

  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }

  if (c && typeof c.getRandomValues === "function") {
    const buf = new Uint8Array(16);
    c.getRandomValues(buf);
    buf[6] = (buf[6] & 0x0f) | 0x40; // v4
    buf[8] = (buf[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join(
      ""
    );
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16
    )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Non-crypto fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
